import moment from "moment";
import { useState } from "react";

import { ProgressPoint } from "api/memberPortal/project/progressPoints/progressPoints.types";
import { ExpandedEcrAssignmentRow } from "api/memberPortal/reports/expandedEcrAssignment/expandedEcrAssignment.types";
import { useAuth } from "hooks/useAuth";
import { formatProgressPointsForExport } from "utils/progressPointUtils";
import { calculateActiveDaysInProgress } from "utils/timeUtils";

import type { GridRowModel } from "@mui/x-data-grid";

interface ExportData extends ExpandedEcrAssignmentRow {
  progressPoints?: ProgressPoint[];
}

export const useExpandedEcrExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [alert, setAlert] = useState({ message: "", type: "" });
  const { authToken, csrfToken } = useAuth();

  const exportToCsv = async (
    rows: GridRowModel[],
    filename: string = "expanded-ecr-assignment-report",
  ) => {
    setIsExporting(true);

    try {
      // Get progress points for all rows that have product_ids
      const progressPointPromises = rows
        .filter((row: any) => row.product_id)
        .map(async (row: any) => {
          try {
            const response = await fetch(
              `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/progress_points_by_pid/?pid=${row.product_id}`,
              {
                headers: {
                  Authorization: `Token ${authToken}`,
                  "Content-Type": "application/json",
                  "X-CSRFToken": csrfToken,
                },
              },
            );

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Sort the results like the API does
            const sortedResult = result.sort(
              (a: ProgressPoint, b: ProgressPoint) => {
                return a.progress_point > b.progress_point ? 1 : -1;
              },
            );

            return {
              product_id: row.product_id,
              progressPoints: sortedResult || [],
            };
          } catch (error) {
            console.error(
              `Failed to fetch progress points for product ${row.product_id}:`,
              error,
            );
            return {
              product_id: row.product_id,
              progressPoints: [],
            };
          }
        });

      const progressPointResults = await Promise.all(progressPointPromises);

      // Create a map of product_id to progress points
      const progressPointMap = new Map(
        progressPointResults.map(
          (result: { product_id: any; progressPoints: ProgressPoint[] }) => [
            result.product_id,
            result.progressPoints,
          ],
        ),
      );

      // Prepare the export data
      const exportData: ExportData[] = rows.map((row: any) => ({
        ...row,
        progressPoints: progressPointMap.get(row.product_id) || [],
      }));

      // Generate CSV content
      const csvContent = generateCsvContent(exportData);

      // Download the file
      downloadCsv(csvContent, filename);
      setAlert({
        message: "CSV exported successfully!",
        type: "success",
      });
    } catch (error) {
      console.log("error exporting csv: ", error);
      setAlert({
        message: "Unable to export CSV. Please try again.",
        type: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const clearAlert = () => {
    setAlert({ message: "", type: "" });
  };

  return {
    exportToCsv,
    isExporting,
    alert,
    clearAlert,
  };
};

const generateCsvContent = (data: ExportData[]): string => {
  const headers = [
    "VID",
    "Lab",
    "Project Team",
    "Conformance",
    "Status",
    "Tech Type",
    "Progress Points",
    "Days in Progress",
    "Latest Note",
  ];

  const escapeQuotes = (str: string) => str.replace(/"/g, '""');
  const wrapInQuotes = (str: string) => `"${escapeQuotes(str)}"`;

  const csvRows = [
    headers.join(","),
    ...data.map((row) => {
      const projectTeam = row.project_team
        .map((member) => {
          const name = member.user_id?.non_user_member
            ? member.user_id.non_user_member
            : `${member.user_id?.first_name || ""} ${member.user_id?.last_name || ""}`.trim();
          return member.role ? `${name} (${member.role})` : name;
        })
        .join("\n");

      const conformanceClaims = Array.isArray(row.conformance_claims)
        ? row.conformance_claims.join("\n")
        : row.conformance_claims || "";

      const progressPoints = formatProgressPointsForExport(
        row.progressPoints || [],
        row.evaluator_checkout_date,
        row.validator_checkout_date,
      );

      const daysInProgress = calculateActiveDaysInProgress(row).toString();

      const latestNote = row.latest_note
        ? `${row.latest_note.note.replace(/<[^>]*>/g, "")} - Submitted on ${moment
            .utc(row.latest_note.submitted_on)
            .format("YYYY.MM.DD [at] HHmm")} by ${row.latest_note.submitted_by}`
        : "No notes available";

      return [
        row.v_id || "",
        row.assigned_lab || "",
        wrapInQuotes(projectTeam),
        wrapInQuotes(conformanceClaims),
        row.status || "",
        wrapInQuotes(row.tech_type || ""),
        wrapInQuotes(progressPoints),
        daysInProgress,
        wrapInQuotes(latestNote),
      ].join(",");
    }),
  ];

  return csvRows.join("\n");
};

const downloadCsv = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${filename}-${moment().format("YYYY-MM-DD")}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
